/**
 * Khối "Giới thiệu": mô tả dài, tiện ích, liên hệ.
 *
 * Địa chỉ và giờ mở cửa không nằm ở đây mà ở hero — đó là hai thứ khách mở
 * trang để xem, không nên bắt cuộn xuống mới thấy.
 *
 * Mô tả là rich text đã sanitize bên backend nên đặt thẳng qua
 * dangerouslySetInnerHTML — FO không sanitize lại, cũng không tự thêm thẻ. Thu
 * gọn bằng line-clamp thay vì đo chiều cao: không phải đọc DOM, không nhảy
 * layout khi ảnh trong mô tả tải xong.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import TIcon from "../../components/ui/TIcon";
import { Venue } from "../../interfaces/venue";
import { amenityIcon } from "../../helpers/venue";

interface Props {
  venue: Venue;
}

const AboutSection: React.FC<Props> = ({ venue }) => {
  const [expanded, setExpanded] = useState(false);

  /**
   * Chỉ hiện "Xem thêm" khi mô tả THẬT SỰ dài hơn khung 7 dòng.
   *
   * Trước đây nút hiện bất cứ khi nào có mô tả, nên một quán viết đúng một dòng
   * vẫn có nút "Xem thêm" bấm vào chẳng mở thêm được gì.
   *
   * Phải ĐO chứ không đếm ký tự: số dòng phụ thuộc bề ngang màn hình, cỡ chữ và
   * cả thẻ trong rich text. So scrollHeight với clientHeight lúc đang bị cắt là
   * câu hỏi đúng - "cắt đi có mất chữ nào không".
   */
  const moTaRef = useRef<HTMLDivElement>(null);
  const [biCat, setBiCat] = useState(false);

  const doLaiChieuCao = useCallback(() => {
    const el = moTaRef.current;
    if (!el) {
      return;
    }
    // Đo lúc đang cắt: mở rồi thì hai con số bằng nhau, hỏi cũng vô nghĩa.
    if (el.classList.contains("clamped")) {
      setBiCat(el.scrollHeight > el.clientHeight + 1);
    }
  }, []);

  useEffect(() => {
    doLaiChieuCao();

    // Xoay ngang máy hay đổi cỡ cửa sổ là số dòng đổi theo. ResizeObserver bắt cả
    // trường hợp ảnh trong mô tả tải xong làm khối cao lên, thứ mà sự kiện resize
    // của cửa sổ không thấy.
    const el = moTaRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", doLaiChieuCao);
      return () => window.removeEventListener("resize", doLaiChieuCao);
    }
    const quanSat = new ResizeObserver(doLaiChieuCao);
    quanSat.observe(el);
    return () => quanSat.disconnect();
  }, [doLaiChieuCao, venue.description]);

  const contacts: { icon: string; label: string; href: string }[] = [];
  if (venue.contacts.fanpage) {
    contacts.push({
      icon: "brand-facebook",
      label: venue.contacts.fanpage,
      href: venue.contacts.fanpage,
    });
  }
  if (venue.contacts.website) {
    contacts.push({
      icon: "world",
      label: venue.contacts.website,
      href: venue.contacts.website,
    });
  }
  if (venue.contacts.email) {
    contacts.push({
      icon: "mail",
      label: venue.contacts.email,
      href: `mailto:${venue.contacts.email}`,
    });
  }

  return (
    <section className="vn-section" id="gioi-thieu">
      <h2 className="vn-section-title">Giới thiệu</h2>

      {venue.description ? (
        <>
          <div
            ref={moTaRef}
            className={`vn-rich${expanded ? "" : " clamped"}`}
            dangerouslySetInnerHTML={{ __html: venue.description }}
          />
          {biCat ? (
            <button
              type="button"
              className="vn-more"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? "Thu gọn" : "Xem thêm"}
            </button>
          ) : null}
        </>
      ) : null}

      {venue.amenities.length > 0 ? (
        <div className="vn-panel">
          <h3>Tiện ích</h3>
          <div className="vn-amenities">
            {venue.amenities.map((amenity) => (
              <span className="vn-amenity" key={amenity.code}>
                <TIcon name={amenityIcon(amenity.code)} />
                {amenity.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {contacts.length > 0 ? (
        <div className="vn-panel">
          <h3>Liên hệ</h3>
          {contacts.map((contact) => (
            <div className="vn-contact" key={contact.icon + contact.label}>
              <TIcon name={contact.icon} />
              <a
                href={contact.href}
                target={contact.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
              >
                {contact.label}
              </a>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default AboutSection;
